import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import TutorCard from '../common/TutorCard';
import { CaseListCard } from '../common/CaseListCard';
import { useRecommendedCases, useRecommendedTutors } from '../../hooks/api';
import { CarouselItemSkeleton } from '../skeletons';
import type { CaseData, TutorData } from '../../types/api';
import type { SwiperRef } from 'swiper/react';
import { Link, useNavigate } from '@/i18n/navigation';
import { useI18n } from '@/i18n';

interface BrowseSectionProps {
  type: 'tutor' | 'class';
}

const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const BrowseSection: React.FC<BrowseSectionProps> = ({ type }) => {
  const navigate = useNavigate();
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';
  const swiperRef = useRef<SwiperRef>(null);

  useEffect(() => {
    if (!document.getElementById('browse-swiper-styles')) {
      const style = document.createElement('style');
      style.id = 'browse-swiper-styles';
      style.textContent = `
        .browse-swiper .swiper-pagination {
          position: relative !important;
          bottom: auto !important;
          margin-top: 16px;
          text-align: center;
        }
        .browse-swiper .swiper-pagination-bullet {
          width: 8px !important;
          height: 8px !important;
          background-color: #437057 !important;
          opacity: 0.35 !important;
          margin: 0 4px !important;
          transition: all 0.3s ease !important;
          border-radius: 50% !important;
        }
        .browse-swiper .swiper-pagination-bullet-active {
          width: 24px !important;
          height: 8px !important;
          border-radius: 4px !important;
          opacity: 1 !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleViewDetails = useCallback((id: string) => {
    if (type === 'tutor') {
      navigate(`/tutor/${id}?source=recommendation`);
    } else {
      navigate(`/cases?caseId=${id}`);
    }
  }, [type, navigate]);

  // Fetch recommended cases when type is 'class'
  const {
    data: recommendedCasesData,
    isLoading: casesLoading,
    error: casesError,
  } = useRecommendedCases(type === 'class' ? { page: 1, limit: 6 } : undefined);

  // Fetch recommended tutors when type is 'tutor'
  const {
    data: recommendedTutorsData,
    isLoading: tutorsLoading,
    error: tutorsError,
  } = useRecommendedTutors(type === 'tutor' ? { page: 1, limit: 6 } : undefined);

  // Determine loading and error states based on type
  const isLoading = type === 'class' ? casesLoading : tutorsLoading;
  const error = type === 'class' ? casesError : tutorsError;

  // Get raw data for carousel
  const items = useMemo(() => {
    if (type === 'class' && recommendedCasesData?.cases) {
      return recommendedCasesData.cases;
    }
    if (type === 'tutor' && recommendedTutorsData?.tutors) {
      return recommendedTutorsData.tutors;
    }
    return [];
  }, [type, recommendedCasesData, recommendedTutorsData]);

  // Show loading state or error if needed
  if (isLoading) {
    return (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="mb-8 h-14 w-64 animate-pulse rounded-lg bg-gray-100" />
          <div className="flex items-center gap-4 overflow-x-auto pb-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="flex-shrink-0 w-80">
                <CarouselItemSkeleton />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <p className="text-red-500">
              {isZh
                ? type === 'class'
                  ? '个案加载失败'
                  : '导师加载失败'
                : `Failed to load ${type === 'class' ? 'classes' : 'tutors'}`}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="pt-2 md:pt-3 pb-12 md:pb-16 bg-white"
    >
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-secondary">
            </div>
            <h2 className="text-4xl font-bold tracking-tight text-primary md:text-5xl">
              {type === 'tutor'
                ? isZh
                  ? '精选认证导师'
                  : 'Top approved tutors'
                : isZh
                ? '精选开放个案'
                : 'Hand-picked open cases'}
            </h2>
          </div>
          <Link
            to={type === 'tutor' ? '/tutors' : '/cases'}
            className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-secondary hover:text-primary transition-colors"
          >
            {isZh ? '查看全部' : 'View all'}
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="relative">
          {/* Left Navigation Button — floats over carousel */}
          <button
            onClick={() => swiperRef.current?.swiper.slidePrev()}
            aria-label={
              isZh
                ? type === 'tutor'
                  ? '查看上一组导师'
                  : '查看上一组个案'
                : `View previous ${type === 'tutor' ? 'tutors' : 'cases'}`
            }
            className="absolute left-0 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 rounded-full bg-white p-2.5 shadow-md transition-shadow group hover:shadow-lg md:block md:-translate-x-full md:p-3"
          >
            <ChevronLeft className="h-5 w-5 md:h-6 md:w-6 text-primary group-hover:text-primary-600" />
          </button>

          {/* Carousel Container — clip horizontal overflow only so pagination flows below */}
          <div className="overflow-x-clip">
            <Swiper
              ref={swiperRef}
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={24}
              slidesPerView={1}
              breakpoints={{
                640: {
                  slidesPerView: 2,
                  spaceBetween: 16,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 20,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 24,
                },
              }}
              pagination={{
                clickable: true,
              }}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              loop={items.length > 3}
              className="browse-swiper"
            >
              {type === 'tutor'
                ? (items as TutorData[]).map((tutor) => (
                    <SwiperSlide key={tutor.id} className="h-auto">
                      <div className="px-2">
                        <TutorCard
                          tutor={tutor}
                          isWishlisted={false}
                          onToggleWishlist={() => {}}
                          source="recommendation"
                        />
                      </div>
                    </SwiperSlide>
                  ))
                : (items as CaseData[]).map((caseData) => (
                    <SwiperSlide key={caseData.id} className="h-auto">
                      <div className="px-2">
                        <CaseListCard
                          caseData={caseData}
                          onViewDetails={handleViewDetails}
                        />
                      </div>
                    </SwiperSlide>
                  ))}
            </Swiper>
          </div>

          {/* Right Navigation Button — floats over carousel */}
          <button
            onClick={() => swiperRef.current?.swiper.slideNext()}
            aria-label={
              isZh
                ? type === 'tutor'
                  ? '查看下一组导师'
                  : '查看下一组个案'
                : `View next ${type === 'tutor' ? 'tutors' : 'cases'}`
            }
            className="absolute right-0 top-1/2 z-10 hidden translate-x-1/2 -translate-y-1/2 rounded-full bg-white p-2.5 shadow-md transition-shadow group hover:shadow-lg md:block md:translate-x-full md:p-3"
          >
            <ChevronRight className="h-5 w-5 md:h-6 md:w-6 text-primary group-hover:text-primary-600" />
          </button>
        </div>

        <div className="mt-6 md:hidden">
          <Link
            to={type === 'tutor' ? '/tutors' : '/cases'}
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-secondary/15 bg-secondary/5 px-4 py-3 text-sm font-semibold text-secondary transition-colors hover:border-secondary/25 hover:bg-secondary/10 hover:text-primary"
          >
            <span>View more</span>
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </motion.section>
  );
};

export default BrowseSection;
